import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import z from 'zod';
import { privateHttpClient } from '../httpClient';
import { curationsApiPath } from './apiPath';

const CurationSnapV2GoodsItemSchema = z
  .object({
    goodsNo: z.union([z.string(), z.number()]).describe('상품 번호'),
    goodsName: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    brandLinkUrl: z.string(),
    linkUrl: z.string(),
    imageUrl: z.string(),
    normalPrice: z.number().int(),
    price: z.number().int(),
    couponPrice: z.number().int().nullish(),
    couponSalesRate: z.number().int().nullish(),
    extraDiscountAmount: z
      .number()
      .int()
      .nullish()
      .describe('추가할인 금액(원). 표시는 finalPrice 사용 (주문 도메인 전용, MUSINSA 전용)'),
    finalPrice: z
      .number()
      .int()
      .nullish()
      .describe('최종가 = 세일가 + 상품쿠폰 + 추가할인 (장바구니쿠폰 제외, MUSINSA 전용)'),
    finalDiscount: z
      .number()
      .int()
      .nullish()
      .describe('정상가 대비 finalPrice 할인율 (MUSINSA 전용)'),
    isLowestPrice: z
      .boolean()
      .nullish()
      .describe('RADAR 추가할인 최저가 보장 여부 — true 시 최저가도전 라벨 (MUSINSA 전용)'),
    isExclusive: z.boolean().nullish().describe('단독 상품 여부 (MUSINSA 전용)'),
    salesRate: z.number().int(),
    isLiked: z.boolean(),
    isSoldOut: z.boolean(),
    hasOptionPrice: z.boolean().nullish(),
  })
  .describe('CurationSnapV2GoodsItemSchema');

const CurationSnapV2SnapItemSchema = z
  .object({
    id: z.union([z.string(), z.number()]),
    /** 단일 이미지(구 스펙) */
    imageUrl: z.string().nullish(),
    /** 다중 이미지 */
    imageUrls: z.array(z.string()).optional(),
    linkUrl: z.string(),
    goods: z.array(CurationSnapV2GoodsItemSchema),
  })
  .describe('CurationSnapV2SnapItemSchema');

const GetCurationSnapsV2DataSchema = z
  .object({
    curationId: z.string(),
    title: z.string(),
    description: z.string().nullish(),
    list: z.array(CurationSnapV2SnapItemSchema),
  })
  .describe('GetCurationSnapsV2DataSchema');

const GetCurationSnapsResponseSchema = z
  .object({
    data: GetCurationSnapsV2DataSchema,
    meta: z.object({
      result: z.enum(['FAIL', 'SUCCESS']),
      errorCode: z.string().nullable(),
      message: z.string().nullable(),
    }),
    link: z
      .object({
        next: z.string().nullable().optional(),
        prev: z.string().nullable().optional(),
        total: z.number().optional(),
      })
      .optional()
      .nullable(),
  })
  .describe('GetCurationSnapsResponseSchema');

export type GetCurationSnapsResponse = z.infer<typeof GetCurationSnapsResponseSchema>;

const GetCurationSnapsRequestSchema = z
  .object({
    curationId: z.string(),
    page: z.number().int().min(1),
    size: z.number().int().min(1).max(100),
    snapId: z.string().optional(),
  })
  .describe('GetCurationSnapsRequestSchema');

export type GetCurationSnapsRequest = z.infer<typeof GetCurationSnapsRequestSchema>;

export const getCurationSnaps = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetCurationSnapsRequestSchema),
    async (params: GetCurationSnapsRequest): Promise<GetCurationSnapsResponse> =>
      executeStandardRequest(async () => {
        const { curationId, page, size, snapId } = params;
        const query: Record<string, string | number> = {
          page,
          size,
          ...(snapId !== undefined ? { snapId } : {}),
        };
        try {
          return privateHttpClient.get<GetCurationSnapsResponse>(
            curationsApiPath.getCurationSnaps(curationId),
            query,
          );
        } catch (error) {
          console.error(error);
          throw error;
        }
      }),
    validateApiResponse(GetCurationSnapsResponseSchema),
  ),
);
